import { Toaster, default as onToast } from "react-hot-toast";
import styled from "styled-components";

import Image from "@vfuk/core-image";
import Paragraph from "@vfuk/core-paragraph";
import Spacing from "@vfuk/core-spacing";
import type { StateNotificationProps } from "@vfuk/core-state-notification/dist/StateNotification.types";

import { Stack } from "../Stack";
import { Tooltip } from "../Tooltip";
import close from "../assets/svg/cross.svg";
import error from "../assets/svg/error.svg";
import refresh from "../assets/svg/refresh.svg";
import success from "../assets/svg/success.svg";
import classes from "./Toast.module.scss";

export type ToastColor = "success" | "error" | "warn" | "incomplete" | "info";

type ToastProps = {
  text: string | null;
  state: StateNotificationProps["state"];
  onUndo?: () => void;
  isFullWidth?: boolean;
};

// adds the necessary 'toaster' context - this should be added to the 'App.tsx' file
export const ToastContext = (): JSX.Element => (
  <Toaster
    containerStyle={{
      padding: 0,
      background: "transparent",
    }}
    toastOptions={{
      style: {
        padding: 0,
        background: "transparent",
        boxShadow: "0px 10px 9px rgb(0 0 0 / 25%), 0 3px 3px rgb(0 0 0 / 25%)",
      },
    }}
    position="bottom-center"
  />
);

const getColor = (state: ToastColor) => {
  switch (state) {
    case "success":
      return "rgb(0, 138, 0)";
    case "error":
      return "rgb(189, 0, 0)";
    case "warn":
      return "rgb(235,151,0)";
    case "incomplete":
      return "rgb(190, 190, 190)";
    case "info":
      return "rgb(0, 94, 165)";
    default:
      return "rgb(189, 0, 0)";
  }
};

const UnstyledCircleButton = styled.button`
  background: none;
  border: solid #888888 1px;
  border-radius: 100%;
  cursor: pointer;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: right;
  padding: 0;
`;

const ParagraphWithLineBreak = styled.span`
  line-break: anywhere;
`;

const StyledContainer = styled.div<Pick<ToastProps, "isFullWidth" | "state">>`
  width: ${({ isFullWidth }) => (isFullWidth ? "75%" : "auto")};
  border-color: ${({ state }) => getColor(state)};
`;

const StyledToastIcon = styled.div<Pick<ToastProps, "state">>`
  background-color: ${({ state }) => getColor(state)};
`;

const StyledTooltipSpan = styled.span`
  cursor: pointer;
`;

// headless toast function for displaying a toast
export const toast = ({
  text,
  state,
  onUndo,
  // quick way as a developer to make the toast full-width depending on changing UX requirements.
  isFullWidth = true,
}: ToastProps) => {
  onToast.custom((t) => (
    <StyledContainer
      isFullWidth={isFullWidth}
      state={state}
      className={classes.Toast}
    >
      <Stack width="100%" justifyContent="space-between">
        <div>
          <Stack>
            <StyledToastIcon className={classes.ToastIcon} state={state}>
              {state && (
                <img
                  alt="Toast Icon"
                  src={state === "success" ? success : error}
                  width="28px"
                  height="28px"
                />
              )}
            </StyledToastIcon>

            <Spacing spacingLevel={{ left: 4, right: 4 }}>
              <ParagraphWithLineBreak>
                <Paragraph>{text}</Paragraph>
              </ParagraphWithLineBreak>
            </Spacing>
          </Stack>
        </div>

        <Stack>
          {onUndo && (
            <Spacing spacingLevel={{ right: 4 }}>
              <Tooltip content="Undo">
                <StyledTooltipSpan
                  onClick={() => {
                    onUndo();
                    onToast.remove(t.id); // Remove the toast after the action has been completed
                  }}
                >
                  <img
                    alt="Undo or Retry Action"
                    src={refresh}
                    width="28.5px"
                    height="28.5px"
                  />
                </StyledTooltipSpan>
              </Tooltip>
            </Spacing>
          )}

          <Spacing spacingLevel={{ right: 4 }}>
            <UnstyledCircleButton onClick={() => onToast.remove(t.id)}>
              <Image
                alt="Close Toast"
                sm={{
                  src: close,
                }}
              />
            </UnstyledCircleButton>
          </Spacing>
        </Stack>
      </Stack>
    </StyledContainer>
  ));
};
